import clsx from "clsx";
import { ReceiptText, Wifi, WifiOff } from "lucide-react";
import { Link } from "wouter";

import { useBetSlip } from "@/betslip/BetSlipProvider";
import { useCashier } from "@/cashier/CashierProvider";
import { WalletControl } from "@/cashier/WalletControl";
import { useChat } from "@/chat/ChatProvider";
import { Button } from "@/components/ui";
import { useRealtime } from "@/realtime/RealtimeProvider";
import { useSession } from "@/session/SessionProvider";
import { resolveThemeAsset } from "@/theme/assets";
import { useTheme } from "@/theme/ThemeProvider";

import { AccountMenu, type AccountBalanceView } from "./AccountMenu";

export function V2Header() {
  const { theme } = useTheme();
  const { mode } = useRealtime();
  const {
    session,
    loading,
    logout,
    registrationEnabled,
    requestLogin,
    requestRegistration,
  } = useSession();
  const chat = useChat();
  const betslip = useBetSlip();
  const cashier = useCashier();
  const mark = resolveThemeAsset(theme.brand.markAssetKey);
  const wordmark = resolveThemeAsset(theme.brand.wordmarkAssetKey);
  const compactBrand = wordmark ?? mark;
  const hasWalletBalance = cashier.selectedWallet !== null;
  const accountBalanceState: AccountBalanceView["state"] =
    cashier.state === "loading"
      ? "loading"
      : !hasWalletBalance
        ? "unavailable"
        : cashier.selectedWallet?.stale
          ? "stale"
          : "ready";
  const accountBalance: AccountBalanceView = {
    cash: cashier.selectedWallet?.cash ?? "—",
    bonus: cashier.selectedWallet?.bonus ?? "—",
    locked: cashier.selectedWallet?.locked ?? "—",
    state: accountBalanceState,
  };

  return (
    <header className={`v2-header${session ? " is-authenticated" : ""}`}>
      <div className="v2-header__left">
        <Link
          aria-label={`${theme.brand.displayName} home`}
          className={clsx(
            "v2-header__mark",
            wordmark && "v2-header__mark--wordmark",
          )}
          href="/app/"
        >
          {compactBrand ? (
            <img alt={theme.brand.logoAlt} src={compactBrand} />
          ) : (
            <span>{theme.brand.shortName}</span>
          )}
        </Link>
      </div>

      <Link
        aria-label={`${theme.brand.displayName} home`}
        className="v2-header__wordmark"
        href="/app/"
      >
        {wordmark ? (
          <img alt={theme.brand.logoAlt} src={wordmark} />
        ) : (
          <span>{theme.brand.displayName}</span>
        )}
      </Link>

      <div className="v2-header__actions">
        {session ? (
          <>
            <button
              aria-controls="betslip-drawer"
              aria-expanded={betslip.open}
              aria-label={`Open Betslip, ${betslip.legs.length} selections`}
              className="v2-betslip-button"
              onClick={(event) => betslip.setOpen(true, event.currentTarget)}
              type="button"
            >
              <ReceiptText aria-hidden />
              <span className="v2-betslip-button__label">Betslip</span>
              {betslip.legs.length ? (
                <span className="v2-betslip-button__count">
                  {betslip.legs.length}
                </span>
              ) : null}
            </button>

            <WalletControl />

            <AccountMenu
              balance={accountBalance}
              displayName={session.displayName}
              onLogout={logout}
            />
          </>
        ) : (
          <div
            aria-label={loading ? "Loading account" : undefined}
            className="v2-header__auth-actions"
            role={loading ? "status" : undefined}
          >
            {loading ? (
              <>
                <span aria-hidden className="v2-header__auth-loading" />
                <span aria-hidden className="v2-header__auth-loading" />
              </>
            ) : (
              <>
                <Button
                  className="v2-header__sign-in"
                  onClick={requestLogin}
                  variant="secondary"
                >
                  Sign In
                </Button>
                {registrationEnabled ? (
                  <Button
                    className="v2-header__register"
                    onClick={requestRegistration}
                  >
                    Register
                  </Button>
                ) : null}
              </>
            )}
          </div>
        )}

        <span className="v2-header__divider" aria-hidden />
        <button
          aria-expanded={chat.isOpen}
          aria-label="Social"
          className="v2-chat-button"
          onClick={chat.toggle}
          type="button"
        >
          <img
            alt=""
            aria-hidden
            src="/app/static/themes/monkeytilt/icons/message.svg"
          />
          {chat.snapshot.unreadCount > 0 ? (
            <span className="v2-chat-button__badge">
              {chat.snapshot.unreadMentions
                ? "@"
                : Math.min(chat.snapshot.unreadCount, 99)}
            </span>
          ) : null}
        </button>
        <span
          aria-label={`Realtime: ${mode}`}
          className={clsx("v2-realtime", "v2-realtime--sr-only", `is-${mode}`)}
          role="status"
          title={`Realtime: ${mode}`}
        >
          {mode === "live" ? <Wifi aria-hidden /> : <WifiOff aria-hidden />}
          <span>{mode}</span>
        </span>
      </div>
    </header>
  );
}
